import  { useState, useEffect } from 'react';
import { Users, Wifi, DollarSign, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useApp } from "@/contexts/AppContext";
import {DASHBOARD, USER_USERS, HOTSPOT, GROWTH, EXPORT_USER} from '../../api/services/adminservice';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from "@/utils/errorUtils";
const AdminOverview = () => {
  const [activeTab, setActiveTab] = useState('day');
  const { theme } = useApp();
  const { t } = useTranslation();

  const PAGE_SIZE = 10;

  const stats = [
    { icon: Users, value: '2,543', label: t('admin.overview.stats.totalUsers', { defaultValue: 'Total Users' }), change: '+12.5%', positive: true },
    { icon: Wifi, value: '1,875', label: t('admin.overview.stats.activeHotspots', { defaultValue: 'Active Hotspots' }), change: '+4.8%', positive: true },
    { icon: DollarSign, value: '$12,450', label: t('admin.overview.stats.pendingPayouts', { defaultValue: 'Pending Payouts' }), change: t('admin.overview.stats.pending', { defaultValue: 'Pending' }), positive: null },
    { icon: RefreshCw, value: '324', label: t('admin.overview.stats.dailyReferrals', { defaultValue: 'Daily Referrals' }), change: '+16.2%', positive: true }
  ];

  const isDark = theme === 'dark';
  let [dash, setdash] = useState(null);
  let [user, setusers] = useState(null);
  let [hotspotState, sethotspots] = useState(null);
  let [growths, setgrowth] = useState(null);
  let [pagination, setpagination] = useState(null);
  let [totalpage, settotalpage] = useState(null);
  let [currentpag, setcurrentpag] = useState(1);

  // Build initials from a name like "Paul Uhunoma" -> "PU"
  function initialsFromName(name){
    if (!name) return 'U';
    const parts = String(name).trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || 'U';
  }

  function isImageUrl(v){
    return typeof v === 'string' && /^https?:\/\//i.test(v);
  }

function getPagination(currentPage, totalPages) {
  const MAX_VISIBLE = 9;

  const range = (start, end) =>
    Array.from({ length: end - start + 1 }, (_, i) => start + i);

  if (totalPages <= MAX_VISIBLE) {
    return range(1, totalPages);
  }

  const pages = [];

  pages.push(...range(1, 4));

  if (currentPage > 5) {
    pages.push("...");
  }

  const middleStart = Math.max(5, currentPage - 1);
  const middleEnd = Math.min(totalPages - 4, currentPage + 1);

  pages.push(...range(middleStart, middleEnd));

  if (currentPage < totalPages - 4) {
    pages.push("...");
  }

  pages.push(...range(totalPages - 3, totalPages));

   return [...new Set(pages)];
}

  async function dashboard(){
    let response = await DASHBOARD();
    setdash(response.data);
  }

  // Fetch a specific page of users from the paginated admin endpoint.
  async function users(page = 1){
    let response = await USER_USERS({ page, limit: PAGE_SIZE });
    let data = (response as unknown as { data?: { users?: unknown[], pagination?: { currentPage?: number, totalPages?: number } } }).data;
    setusers((data?.users as unknown[]) || []);
    let pag = data?.pagination;
    if (pag) {
      const paginationArray = getPagination(pag.currentPage as number, pag.totalPages as number);
      setpagination(paginationArray);
      setcurrentpag(pag.currentPage as number);
      settotalpage(pag.totalPages as number);
    }
  }

  async function hotspot(){
    let response = await HOTSPOT();
    let data = (response as unknown as { data?: { hotspots?: unknown[] } }).data;
    sethotspots((data?.hotspots as unknown[]) || []);
  }
  async function growth(){
    let response = await GROWTH(`?period=${activeTab}`);
    setgrowth(((response as unknown as Record<string, unknown>).data as unknown as Record<string, unknown>).data);
    setActiveTab(((response as unknown as Record<string, unknown>).data as unknown as Record<string, unknown>).period as string);
  }

  // Initial load: dashboard, first page of users, hotspots.
  useEffect(()=>{
    async function render(){
      await dashboard();
      await users(1);
      await hotspot();
    }
    render();
  },[]);

  useEffect(()=> {
      async function fetchGrowth(){
        await growth();
      }
    fetchGrowth();
  }, [activeTab]);

  // Whenever the page changes, fetch THAT page's users from the backend.
  useEffect(()=>{
    if (currentpag == null) {
      return;
    }
    users(currentpag);
  },[currentpag]);

  // Keep the visible page-number list in sync with current/total pages.
  useEffect(()=>{
    if (currentpag == null || totalpage == null) {
      return;
    }
    const paginationArray = getPagination(currentpag, totalpage);
    setpagination(paginationArray);
  },[totalpage]);

  // Jump to a specific page (ignores the "..." separators).
  function goToPage(p){
    if (typeof p !== 'number') return;
    if (totalpage != null && (p < 1 || p > totalpage)) return;
    setcurrentpag(p);
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className={`${isDark ? 'bg-[#333436] border border-[#2b2b2c]' : 'bg-white border border-gray-200'} rounded-xl p-6 shadow-sm`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 ${isDark ? 'bg-[#1a1b1e]' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} />
              </div>
              {stat.positive !== null && (
                <span className={`text-xs font-medium px-2 py-1 rounded ${stat.positive ? (isDark ? 'bg-green-900 bg-opacity-30 text-green-400' : 'bg-green-100 text-green-700') : (isDark ? 'bg-red-900 bg-opacity-30 text-red-400' : 'bg-red-100 text-red-700')}`}>
                  {stat.change}
                </span>
              )}
              {stat.positive === null && (
                <span className={`text-xs font-medium px-2 py-1 rounded ${isDark ? 'bg-yellow-900 bg-opacity-30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                  {stat.change}
                </span>
              )}
            </div>
            <div className={`text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{index === 0 ? dash?.totalUsers  : index === 1 ? dash?.totalHotspots: index === 2 ? dash?.totalPayouts : dash?.totalReferralsToday}</div>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{stat?.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Growth Chart */}
        <div className={`lg:col-span-2 ${isDark ? 'bg-[#333436] border border-[#2b2b2c]' : 'bg-white border border-gray-200'} rounded-xl p-6 shadow-sm`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('admin.overview.userGrowth', { defaultValue: 'User Growth' })}</h2>
            <div className={`flex space-x-1 ${isDark ? 'bg-[#1a1b1e]' : 'bg-gray-300'} rounded-lg p-1`}>
              {['day', 'week', 'month'].map(tab => (
                <button 
                  key={tab} 
                  onClick={() => setActiveTab(tab)} 
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === tab ? 'dark:bg-blue-600 bg-black text-white shadow-sm' : `${isDark ? 'text-gray-400' : 'text-black hover:text-gray-900'}`}`}
                >
                  {tab === 'day'
                    ? t('admin.common.day', { defaultValue: 'Day' })
                    : tab === 'week'
                      ? t('admin.common.week', { defaultValue: 'Week' })
                      : t('admin.common.month', { defaultValue: 'Month' })}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={growths}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} vertical={false} />
              <XAxis 
                dataKey="label" 
                axisLine={{ stroke: isDark ? '#4a4a4c' : '#e5e7eb' }}
                tickLine={false} 
                tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }} 
              />
              <YAxis 
               axisLine={{ stroke: isDark ? '#4a4a4c' : '#e5e7eb' }}
                tickLine={false} 
                tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }} 
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDark ? '#333436' : '#ffffff', 
                  border: `1px solid ${isDark ? '#2b2b2c' : '#e5e7eb'}`, 
                   boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px', 
                  color: isDark ? '#ffffff' : '#000000' 
                }} 
              />
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#6366f1" 
                strokeWidth={3} 
                fillOpacity={1}
                dot={true} 
                fill="url(#colorUsers)" 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Performing Hotspots */}
        <div className={`${isDark ? 'bg-[#333436] border border-[#2b2b2c]' : 'bg-white border border-gray-200'} rounded-xl p-6 shadow-sm`}>
          <h2 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('admin.overview.topPerformingHotspots', { defaultValue: 'Top Performing Hotspots' })}</h2>
          <div className="space-y-4">
            {hotspotState?.map((hotspot: { indicator?: string; name?: string; performanceScore?: number }, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full ${hotspot.indicator === 'green' ? 'bg-green-500' : hotspot.indicator === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{hotspot.name}</span>
                </div>
                <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{hotspot.performanceScore}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Users Table */}
      <div className={`${isDark ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-white border-gray-200'} rounded-xl border shadow-sm`}>
        <div className={`p-6 border-b ${isDark ? 'border-[#2b2b2c]' : 'border-gray-200'} flex items-center justify-between`}>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('admin.overview.recentUsers', { defaultValue: 'Recent Users' })}</h2>
          <button onClick={async ()=>{
            let response = await EXPORT_USER();
            let data = (response as unknown as { data?: { downloadUrl?: string; fileName?: string } }).data;
            console.log(data);
            let a = document.createElement('a');
            a.href = data?.downloadUrl as string;
            a.download = (data?.fileName as string) || 'users_export.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }} className="px-4 py-2 dark:bg-blue-600 bg-black text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
            {t('admin.overview.exportCsv', { defaultValue: 'Export CSV' })}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${isDark ? 'bg-[#1a1b1e] border-[#2b2b2c]' : 'bg-gray-50 border-gray-200'} border-b`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>{t('admin.overview.table.user', { defaultValue: 'User' })}</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>{t('admin.overview.table.status', { defaultValue: 'Status' })}</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>{t('admin.overview.table.location', { defaultValue: 'Location' })}</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>{t('admin.overview.table.lastActive', { defaultValue: 'Last Active' })}</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>{t('admin.overview.table.actions', { defaultValue: 'Actions' })}</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-[#2b2b2c]' : 'divide-gray-200'}`}>
              {user?.map((user, index) => (
                <tr key={index} className={`${isDark ? '' : 'hover:'} transition-colors`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="relative w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm overflow-hidden">
                        <span className="absolute inset-0 flex items-center justify-center">
                          {!isImageUrl(user.avatar) && user.avatar ? user.avatar : initialsFromName(user.name)}
                        </span>
                        {isImageUrl(user.avatar) && (
                          <img
                            src={user.avatar}
                            alt={user.name || 'User'}
                            className="absolute inset-0 w-full h-full object-cover z-10"
                            onError={(ev)=>{
                              ev.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name}</div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                      (user.status || '').toLowerCase() === 'active' ? (isDark ? 'bg-green-900 bg-opacity-30 text-green-400' : 'bg-green-100 text-green-700') :
                      (user.status || '').toLowerCase() === 'pending' ? (isDark ? 'bg-yellow-900 bg-opacity-30 text-yellow-400' : 'bg-yellow-100 text-yellow-700') :
                      (isDark ? 'bg-red-900 bg-opacity-30 text-red-400' : 'bg-red-100 text-red-700')
                    }`}>
                      {user.status ? t(`admin.common.${user.status.toLowerCase()}`, { defaultValue: user.status.charAt(0).toUpperCase() + user.status.slice(1) }) : ''}
                    </span>
                  </td>
                  <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{user.location || 'N/A'}</td>
                  <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'N/A'}</td>
                  <td className="px-6 py-4">
                    <button className="text-sm dark:text-gray-300 text-black hover:text-blue-700 font-medium transition-colors">
                      {t('admin.overview.table.viewDetails', { defaultValue: 'View Details' })}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
         </div>
        <div className={`px-6 py-4 flex items-center justify-center space-x-2 ${isDark ? 'border-[#2b2b2c]' : 'border-gray-200'}`}>
          <button onClick={()=> goToPage(currentpag <= 1 ? 1 : currentpag - 1)} className={`w-8 h-8 flex items-center justify-center border rounded transition-colors ${isDark ? 'border-[#2b2b2c] text-gray-400 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            &lt;
          </button>

          {pagination?.map((e, idx)=> (<button key={idx} disabled={e === '...'} onClick={()=> goToPage(e)} className={`w-8 h-8 flex items-center justify-center border rounded transition-colors ${currentpag === e
                    ? 'dark:bg-blue-600 bg-black text-white'
                    : isDark 
                      ? 'border border-[#2b2b2c] text-gray-400 hover:bg-gray-800' 
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'} `}>
            {e}
          </button>))}
          <button onClick={()=> goToPage(totalpage != null && currentpag >= totalpage ? totalpage : currentpag + 1)} className={`w-8 h-8 flex items-center justify-center border rounded transition-colors ${isDark ? 'border-[#2b2b2c] text-gray-400 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            &gt;
          </button>
          <button onClick={()=> goToPage(totalpage != null && currentpag >= totalpage ? totalpage : currentpag + 1)} className={`ml-2 px-3 h-8 flex items-center justify-center border rounded text-sm transition-colors ${isDark ? 'border-[#2b2b2c] text-gray-400 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            {t('admin.overview.pagination.next', { defaultValue: 'Next' })}
          </button>
       
      </div>
    </div>
  );
};

export default AdminOverview;
